/**
 * Pure helpers for useSavedFilters `isSnapshotEmpty` — return true when a snapshot has no
 * meaningful criteria (saving should be blocked).
 */

export function isServiceTicketFilterSnapshotEmpty(snapshot) {
  const hasDba = (snapshot.dba?.length ?? 0) > 0;
  const hasProblem = Boolean(snapshot.problemType);
  const hasOwner = Boolean(snapshot.ownerGroup);
  const hasDates = Boolean(snapshot.created?.from || snapshot.created?.to);
  const hasStatus = (snapshot.status?.length ?? 0) > 0;

  return (
    !hasDba &&
    !hasProblem &&
    !hasOwner &&
    !hasDates &&
    !hasStatus
  );
}

export function isResidualFilterSnapshotEmpty(snapshot) {
  const hasOrg = Boolean(snapshot.orgFilter);
  const hasOffices = (snapshot.offices?.length ?? 0) > 0;
  const hasBranches = (snapshot.branches?.length ?? 0) > 0;
  const hasRepCodes = (snapshot.repCodes?.length ?? 0) > 0;

  return (
    !hasOrg && !hasOffices && !hasBranches && !hasRepCodes
  );
}

export function isDashboardFilterSnapshotEmpty(snapshot) {
  const hasCards = (snapshot.cards?.length ?? 0) > 0;
  const hasOrg = Boolean(snapshot.filterOrgTemp);
  const hasUser = Boolean(snapshot.filterUserTemp);
  const hasGoal = Boolean(
    snapshot.goalFilterKeyTemp && String(snapshot.goalFilterKeyTemp).length,
  );

  return !hasCards && !hasOrg && !hasUser && !hasGoal;
}

export function isApplicationsFilterSnapshotEmpty(snapshot) {
  const emptyArr = (a) => !a || a.length === 0;

  return (
    emptyArr(snapshot.left_progression_filter) &&
    emptyArr(snapshot.manager_id) &&
    emptyArr(snapshot.uw_ids) &&
    emptyArr(snapshot.progression_id) &&
    emptyArr(snapshot.user_id) &&
    !snapshot.organization_id &&
    emptyArr(snapshot.bank) &&
    !snapshot.created_from &&
    !snapshot.created_to &&
    emptyArr(snapshot.rep_codes) &&
    !snapshot.channel &&
    emptyArr(snapshot.ems_office) &&
    emptyArr(snapshot.ems_branch)
  );
}

/**
 * Chargeback drawer: empty when no cards, org, or reasons and amount/days sliders span full bounds.
 * `filterData` comes from the chargeback store (min/max CB amount and days to rebut).
 *
 * @param {object} snapshot — same shape as getCurrentFilterValues (cards, cbAmount, daysToRebut, …)
 * @param {object} filterData — { min_cb_amount, max_cb_amount, min_days_to_rebut, max_days_to_rebut, … }
 */
export function isChargebackFilterSnapshotEmpty(snapshot, filterData) {
  if (
    !filterData ||
    filterData.min_cb_amount === undefined ||
    filterData.max_cb_amount === undefined ||
    filterData.min_days_to_rebut === undefined ||
    filterData.max_days_to_rebut === undefined
  ) {
    return false;
  }

  const hasCards = (snapshot.cards?.length ?? 0) > 0;
  const hasOrg = Boolean(snapshot.orgFilter);
  const hasReasons = (snapshot.reasons?.length ?? 0) > 0;
  if (hasCards || hasOrg || hasReasons) {
    return false;
  }

  const n = (v) => Number(v);
  const amt = snapshot.cbAmount || { from: 0, to: 0 };
  const amountIsFull =
    n(amt.from) === n(filterData.min_cb_amount) &&
    n(amt.to) === n(filterData.max_cb_amount);

  const days = snapshot.daysToRebut || { from: 0, to: 0 };
  const daysIsFull =
    n(days.from) === n(filterData.min_days_to_rebut) &&
    n(days.to) === n(filterData.max_days_to_rebut);

  if (!amountIsFull || !daysIsFull) {
    return false;
  }

  return true;
}
